import { HeartPulse, Wallet } from 'lucide-react';
import InsuranceCard from 'components/Profile/Insurance/Card';
import { MEDICAL_GROUPS } from 'components/Profile/MedicalHistory/helper';
import { hideAllergies } from 'components/Profile/helper';
import { capitalize } from 'lodash';
import { formatDate, formatPhone } from 'utils/helpers';
import { getPatient, getPractice } from 'library/auth';

const join = (parts, separator = ' ') => parts.filter(Boolean).join(separator);

export const checkedNames = (group) =>
  group?.data?.filter(({ attributes }) => attributes.status).map(({ attributes }) => attributes.name) ?? [];

const personalSummary = (attributes) => {
  const { first_name, middle_name, last_name, birth_date, gender, email, mobile_phone, address_line1, address_line2, city, state, zip_code, emergency_contacts } = attributes || {};

  const hideMiddleName = getPractice()?.data_provider === 'velox';
  const contacts = emergency_contacts?.data?.map(({ attributes: a }) => a) ?? [];

  return {
    sections: [
      {
        title: 'Legal name',
        fields: [
          {
            label: 'Full name',
            value: join([first_name, hideMiddleName ? '' : middle_name, last_name]),
          },
          { label: 'Date of birth', value: birth_date ? formatDate(birth_date, 'medium') : '' },
          { label: 'Gender', value: capitalize(gender) },
        ],
      },
      {
        title: 'Contact information',
        fields: [
          { label: 'Email', value: email },
          { label: 'Mobile phone', value: mobile_phone ? formatPhone(mobile_phone) : '' },
        ],
      },
      {
        title: 'Home address',
        fields: [
          { label: 'Street', value: join([address_line1, address_line2 && `Apt ${address_line2}`], ', ') },
          { label: 'City, state & ZIP', value: join([join([city, state], ', '), zip_code]) },
        ],
      },
      {
        title: 'Emergency contact',
        empty: "No one added — hit edit if you'd like us to have a backup contact.",
        fields: contacts.map((contact) => ({
          label: contact.relationship || 'Contact',
          value: join([
            join([contact.first_name, contact.last_name]),
            contact.phone ? formatPhone(contact.phone) : '',
          ], ' · '),
        })),
      },
    ],
  };
};

const insuranceSummary = (attributes) => {
  const { active, card_front_pic, card_back_pic, verification_status } = attributes || {};

  if (!active)
    return {
      empty: {
        icon: Wallet,
        title: "You're marked as self-pay",
        description: "No dental insurance is on file — you'll be billed directly for your visits.",
      },
    };

  return {
    locked: verification_status === 'in_progress', // staff are checking it; no edits until they finish
    card: {
      component: InsuranceCard,
      props: { data: attributes, memberName: getPatient()?.full_name },
      pictures: [
        { label: 'Front of card', url: card_front_pic },
        { label: 'Back of card', url: card_back_pic },
      ],
    },
  };
};

const medicalSummary = (attributes) => {
  const { have_no_medical_conditions, medical_notes } = attributes || {};

  if (have_no_medical_conditions)
    return {
      empty: {
        icon: HeartPulse,
        title: hideAllergies() ? 'No medical conditions' : 'No allergies or medical conditions',
        description: 'You told us none apply — hit edit if anything has changed.',
      },
    };

  return {
    sections: [
      {
        title: 'Health history',
        fields: MEDICAL_GROUPS.filter(({ name }) => !(hideAllergies() && name === 'allergies')).map(({ name, title }) => ({
          label: title,
          value: checkedNames(attributes?.[name]),
          type: 'pills',
          empty: 'None — nothing to add',
          stacked: true,
        })),
      },
      {
        title: 'If you have any medication changes or additions, as well as surgeries or medical conditions that have changed in the last 6 months, list them here.',
        fields: [{ value: medical_notes, stacked: true }],
      },
    ],
  };
};

export const SUMMARY_BUILDERS = {
  personal: personalSummary,
  insurance: insuranceSummary,
  medical: medicalSummary,
};
